/**
 * Middleware 用 公開ルート集約レジストリ
 *
 * 各 feature の `route-config.ts` を static import して、middleware が
 * 認証バイパスすべき path 一覧を組み立てる。
 *
 * 設計方針:
 * - middleware は Edge Runtime で動くため動的 import 不可
 * - 全 feature を static import して中央で集約する形
 * - 各 feature 側は **純定数 export のみ** （関数呼び出し・副作用なし）
 * - 共通 path（/login, /api/auth 等）は本ファイル内で直接定義
 *
 * @see docs/計画/2026-05-09-ICpochipochi-漏れ抽象化.md L-A3
 */

// apps/ 配下の feature
// eslint-disable-next-line gforce/no-lib-to-app-projects-import -- 後続対応 (Issue #1893 で本格 cleanup 予定)
import { CUSTOMER_PORTAL_PUBLIC_PATHS } from '@/app/projects/apps/customer-portal/route-config'
// eslint-disable-next-line gforce/no-lib-to-app-projects-import -- 後続対応 (Issue #1893 で本格 cleanup 予定)
import { ICPOCHIPOCHI_PUBLIC_PATHS } from '@/app/projects/apps/ICpochipochi/route-config'
// eslint-disable-next-line gforce/no-lib-to-app-projects-import -- 後続対応 (Issue #1893 で本格 cleanup 予定)
import { G_PLAN_PUBLIC_PATHS } from '@/app/projects/apps/g-plan/route-config'
// eslint-disable-next-line gforce/no-lib-to-app-projects-import -- 後続対応 (Issue #1893 で本格 cleanup 予定)
import { OPEN_PUBLIC_PATHS } from '@/app/projects/open/route-config'

// app/ 直下の feature
import { INTAKE_PUBLIC_PATHS } from '@/app/intake-v4/route-config'
import { HEARING_PUBLIC_PATHS } from '@/app/hearing/route-config'
import { BUILDING_APPLICATION_PUBLIC_PATHS } from '@/app/building-application/route-config'
import { CUSTOMER_MYPAGE_PUBLIC_PATHS } from '@/app/customer-mypage/route-config'
import { EVENTS_PUBLIC_PATHS } from '@/app/events/route-config'

import { LINE_PUBLIC_PATHS } from '@/app/line/route-config'

// API のみの feature
import { LINEWORKS_PUBLIC_PATHS } from '@/app/api/lineworks/route-config'
import { INVITATIONS_PUBLIC_PATHS } from '@/app/api/invitations/route-config'
import { REFERRALS_PUBLIC_PATHS } from '@/app/api/referrals/route-config'
import { LIFE_X_PUBLIC_PATHS } from '@/app/api/life-x/route-config'
import { EXTERNAL_LIFEX_PUBLIC_PATHS } from '@/app/api/external/lifex/route-config'

/**
 * feature に紐付かない、認証フロー・運用基盤の公開ページパス。
 */
const COMMON_PUBLIC_PAGE_PATHS = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/change-password',
  '/invite',
  '/',
  // Sentry トンネルルート（next.config.ts tunnelRoute: '/monitoring'）
  // セッションクッキーなしで POST されるため認証チェックをバイパスする
  '/monitoring',
] as const

/**
 * feature に紐付かない、認証フロー・運用基盤の公開 API パス。
 */
const COMMON_PUBLIC_API_PATHS = [
  '/api/auth',
  '/api/health',
  '/api/webhooks',
  '/api/cron',
  '/api/setup/claude-hook',
  '/api/open',
  // 日報HTMLプロキシ: Google Chat から認証なしでアクセスされる公開エンドポイント
  '/api/reports/daily',
] as const

/** middleware が認証不要と判定する全公開ページパス */
export const ALL_PUBLIC_PAGE_PATHS: readonly string[] = [
  ...COMMON_PUBLIC_PAGE_PATHS,
  ...CUSTOMER_PORTAL_PUBLIC_PATHS.pages,
  ...ICPOCHIPOCHI_PUBLIC_PATHS.pages,
  ...G_PLAN_PUBLIC_PATHS.pages,
  ...OPEN_PUBLIC_PATHS.pages,
  ...INTAKE_PUBLIC_PATHS.pages,
  ...HEARING_PUBLIC_PATHS.pages,
  ...BUILDING_APPLICATION_PUBLIC_PATHS.pages,
  ...CUSTOMER_MYPAGE_PUBLIC_PATHS.pages,
  ...EVENTS_PUBLIC_PATHS.pages,
  ...LINE_PUBLIC_PATHS.pages,
  ...LINEWORKS_PUBLIC_PATHS.pages,
  ...INVITATIONS_PUBLIC_PATHS.pages,
  ...REFERRALS_PUBLIC_PATHS.pages,
  ...LIFE_X_PUBLIC_PATHS.pages,
]

/** middleware が認証不要と判定する全公開 API パス */
export const ALL_PUBLIC_API_PATHS: readonly string[] = [
  ...COMMON_PUBLIC_API_PATHS,
  ...CUSTOMER_PORTAL_PUBLIC_PATHS.apis,
  ...ICPOCHIPOCHI_PUBLIC_PATHS.apis,
  ...G_PLAN_PUBLIC_PATHS.apis,
  ...OPEN_PUBLIC_PATHS.apis,
  ...INTAKE_PUBLIC_PATHS.apis,
  ...HEARING_PUBLIC_PATHS.apis,
  ...BUILDING_APPLICATION_PUBLIC_PATHS.apis,
  ...CUSTOMER_MYPAGE_PUBLIC_PATHS.apis,
  ...EVENTS_PUBLIC_PATHS.apis,
  ...LINE_PUBLIC_PATHS.apis,
  ...LINEWORKS_PUBLIC_PATHS.apis,
  ...INVITATIONS_PUBLIC_PATHS.apis,
  ...REFERRALS_PUBLIC_PATHS.apis,
  ...LIFE_X_PUBLIC_PATHS.apis,
  ...EXTERNAL_LIFEX_PUBLIC_PATHS.apis,
]
